/**
 * FR-100…108 · Quản lý kết nối Cloudflare.
 *
 * Bố cục: page-head · stat-row · scope-note · card (card-head + table-wrap +
 * card-foot) và bốn modal thêm / sửa / xoay vòng / xoá.
 *
 * ── OQ-264: vì sao bỏ dạng thẻ ─────────────────────────────────────────────
 *
 * Chủ dự án: *"Trang Quản lý kết nối Cloudflare tôi nghĩ cũng nên chuyển về
 * hiển thị theo dạng danh sách giống như các trang tên miền, Nhật ký hoạt
 * động... Để dạng card khi có nhiều kết nối sẽ khó tìm kiếm và phân loại."*
 *
 * `FR-101` viết *"**Thẻ/hàng** kết nối: …"* — tài liệu để ngỏ cả hai dạng, nên
 * đây không phải đổi hợp đồng. Thẻ hợp khi có vài kết nối; càng nhiều thì càng
 * khó vì mỗi thẻ là một khối riêng, mắt không quét dọc theo cột được và không
 * có chỗ tự nhiên để sắp xếp.
 *
 * Giữ nguyên mọi `data-od-id` của prototype (docs/08 §8.9); `conn-<id>` chuyển
 * từ `<article>` sang `<tr>` nên bộ chọn theo id vẫn dùng được.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, thanhApiError, useLoiApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import { CONNECTION_STATUS_CHIP } from '../lib/status';
import { useFormat } from '../lib/format';
import { usePhanTrangUrl } from '../lib/phan-trang';
import type { CfAccountOption, Connection, VerifyConnectionResult } from '../lib/types';
import { Icon } from '../components/Icon';
import { SearchBox } from '../components/SearchBox';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { SortButton } from '../components/SortButton';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { Field, SelectField } from '../components/Field';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { TokenGuide } from '../components/TokenGuide';
import { TunnelsModal } from './TunnelsModal';
import { useToast } from '../components/Toast';

type Dialog =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'edit'; connection: Connection }
  | { kind: 'rotate'; connection: Connection }
  | { kind: 'delete'; connection: Connection }
  | { kind: 'default'; connection: Connection }
  | { kind: 'tunnels'; connection: Connection };

/** Bốn tab lọc — ba trạng thái của `FR-101` cộng "tất cả". */
const PILLS = ['all', 'ok', 'invalid', 'paused'] as const;
type Pill = (typeof PILLS)[number];

/**
 * Các cột sắp xếp được. Khớp với `SortButton` ở tiêu đề cột.
 *
 * OQ-266 — chủ dự án bỏ ô chọn "Mặc định trước" và chuyển sang bấm thẳng vào
 * tiêu đề như trang Tên miền. Thứ tự mặc định (kết nối mặc định lên đầu, rồi
 * theo tên) vẫn còn: đó là lúc URL **không có** `sort`, tức chưa ai bấm cột nào.
 * Không cần một mục riêng trong danh sách để quay về nó.
 *
 * OQ-267 — bản đầu bỏ sót `owner` và `status`. Danh sách này nay phủ **mọi cột
 * có nghĩa**; hai cột còn lại (Account ID, Token) là chuỗi định danh, sắp theo
 * chúng không giúp ai tìm được gì.
 */
const SORT_FIELDS = ['name', 'owner', 'status', 'domains', 'checked'] as const;

/** Nhãn trạng thái, để sắp xếp theo đúng chữ người dùng nhìn thấy. */
export type NhanTrangThai = (status: Connection['status']) => string;

/**
 * Sắp xếp danh sách kết nối — tách khỏi component để test được HÀNH VI.
 *
 * `sort` rỗng nghĩa là giữ nguyên thứ tự máy chủ trả về.
 */
export function sapXepKetNoi(
  danhSach: Connection[],
  sort: string,
  nhanTrangThai?: NhanTrangThai,
): Connection[] {
  const [truong, chieu] = sort.split(':');
  if (!truong || !(SORT_FIELDS as readonly string[]).includes(truong)) return [...danhSach];
  const dau = chieu === 'desc' ? -1 : 1;
  const soSanhChu = (a: string, b: string) => dau * a.localeCompare(b, 'vi');

  const ra = [...danhSach];
  if (truong === 'name') ra.sort((a, b) => soSanhChu(a.name, b.name));
  else if (truong === 'owner') {
    /*
     * Sắp theo đúng chuỗi đang hiện trên màn hình.
     *
     * Ô này rơi về `fullName → email → "—"`, nên nếu sắp theo `owner.fullName`
     * thì mọi kết nối của người chưa đặt họ tên gom vào một chỗ theo một giá trị
     * người dùng không nhìn thấy — thứ tự trông ngẫu nhiên mà không giải thích
     * được.
     */
    const ten = (c: Connection) => c.owner?.fullName ?? c.owner?.email ?? '';
    ra.sort((a, b) => soSanhChu(ten(a), ten(b)));
  } else if (truong === 'status') {
    /*
     * Sắp theo NHÃN, không theo mã.
     *
     * Mã là `ok` / `invalid` / `paused`; nhãn là "Hoạt động" / "Token lỗi" /
     * "Tạm ngưng". Hai thứ tự chữ cái khác hẳn nhau, và người dùng chỉ nhìn
     * thấy nhãn — sắp theo mã cho ra một thứ tự đúng về kỹ thuật nhưng trông
     * như hỏng.
     */
    const nhan = (c: Connection) => nhanTrangThai?.(c.status) ?? c.status;
    ra.sort((a, b) => soSanhChu(nhan(a), nhan(b)));
  } else if (truong === 'domains') ra.sort((a, b) => dau * (a.domainCount - b.domainCount));
  else if (truong === 'checked') {
    /*
     * "Chưa kiểm tra" nằm ở một đầu danh sách.
     *
     * `-Infinity` là cách nói "không có mốc nào" cho đúng ngữ nghĩa. Lưu ý cho
     * người đọc sau: đổi nó thành `0` **không** làm sai thứ tự, vì 0 là năm 1970
     * và mọi mốc thật đều lớn hơn — đó là một đột biến tương đương, đã chạy và
     * xác nhận (OQ-264). Cái thật sự dễ sai ở đây là **chiều** so sánh, nên ca
     * kiểm canh chiều chứ không canh giá trị canh biên.
     */
    const moc = (c: Connection) => (c.lastCheckedAt ? Date.parse(c.lastCheckedAt) : -Infinity);
    ra.sort((a, b) => dau * (moc(a) - moc(b)));
  }
  return ra;
}

export function ConnectionsPage() {
  const { formatNumber } = useFormat();
  const t = useT();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const isAdmin = can('conn.all');
  const canSetDefault = can('conn.default');
  /*
   * OQ-260 — mọi thao tác GHI trên kết nối đòi `conn.own`.
   *
   * Hàng rào route cho vào bằng `conn.own` **hoặc** `conn.all` (docs/05 §5.6),
   * nên có một khe hở: vai trò tuỳ biến mang `conn.all` mà không mang `conn.own`
   * vào được trang, thấy đủ nút, bấm rồi nhận 403 từ API.
   *
   * Bốn vai trò seed không rơi vào tổ hợp ấy — `conn.all` luôn đi kèm
   * `conn.own` — nhưng người quản trị tự tạo vai trò thì tổ hợp nào cũng dựng
   * được. Khoá nút kèm tooltip theo FR-041 thay vì để họ bấm vào khoảng không.
   */
  const canWrite = can('conn.own');

  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const { params, page, perPage, datTrang, datSoDong, datLoc } = usePhanTrangUrl();

  const pill = (PILLS.find((p) => p === params.get('status')) ?? 'all') as Pill;
  const sort = params.get('sort') ?? '';

  const connectionsQuery = useQuery({
    queryKey: ['connections'],
    queryFn: () => api.get<Connection[]>('/connections'),
  });

  const connections = useMemo(() => connectionsQuery.data ?? [], [connectionsQuery.data]);
  const term = query.trim().toLowerCase();

  /*
   * Lọc + sắp xếp phía trình duyệt.
   *
   * `GET /connections` trả một mảng phẳng, không nhận `page`/`perPage` và không
   * trả `meta.total`. Làm phía máy chủ nghĩa là đổi hợp đồng API — việc lớn hơn
   * hẳn điều chủ dự án yêu cầu, và một kết nối là thứ đếm bằng chục chứ không
   * bằng nghìn. Chọn phương án không đụng hợp đồng (OQ-264).
   */
  const visible = useMemo(() => {
    const loc = connections.filter((c) => {
      if (pill !== 'all' && c.status !== pill) return false;
      if (!term) return true;
      return `${c.name} ${c.accountId ?? ''} ${c.owner?.fullName ?? ''} ${c.owner?.email ?? ''}`
        .toLowerCase()
        .includes(term);
    });

    const xep = sapXepKetNoi(loc, sort, (status) => t(`conn.status.${status}`));
    return xep;
  }, [connections, term, pill, sort, t]);
  const total = visible.length;
  const rows = useMemo(
    () => visible.slice((page - 1) * perPage, page * perPage),
    [visible, page, perPage],
  );

  const systemDefault = connections.find((c) => c.isSystemDefault) ?? null;
  const totalZones = connections.reduce((sum, c) => sum + c.domainCount, 0);
  const needAttention = connections.filter((c) => c.status !== 'ok').length;
  const demTheoTrangThai = (p: Pill) =>
    p === 'all' ? connections.length : connections.filter((c) => c.status === p).length;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['connections'] });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const verify = useMutation({
    mutationFn: (id: string) => api.post<VerifyConnectionResult>(`/connections/${id}/verify`),
    onSuccess: async (result) => {
      await invalidate();
      if (result.connection.status === 'invalid') {
        toastError(t('conn.verifiedBad', { name: result.connection.name }));
      } else if (result.missingScopes.length > 0) {
        toastError(t('conn.missingScopes', { list: result.missingScopes.join(', ') }));
      } else {
        toast(
          t('conn.verifiedOk', {
            name: result.connection.name,
            count: formatNumber(result.connection.domainCount),
          }),
        );
      }
    },
    onError,
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => api.post<Connection>(`/connections/${id}/default`),
    onSuccess: async (connection) => {
      await invalidate();
      setDialog({ kind: 'none' });
      toast(t('conn.defaulted', { name: connection.name }));
    },
    onError,
  });

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="cf-title">{t('conn.title')}</h1>
          <p className="lede">{isAdmin ? t('conn.ledeAdmin') : t('conn.ledeUser')}</p>
        </div>
        <div className="row">
          <button
            className="btn"
            type="button"
            onClick={() => void connectionsQuery.refetch()}
            disabled={connectionsQuery.isFetching}
          >
            <Icon name="refresh" size={16} />
            {t('common.retry')}
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setDialog({ kind: 'new' })}
            data-od-id="cf-add"
            disabled={!canWrite}
            title={canWrite ? undefined : t('detail.notAllowed')}
          >
            <Icon name="plus" size={16} />
            {t('conn.add')}
          </button>
        </div>
      </div>

      <div className="stat-row" data-od-id="cf-stats">
        <div className="stat">
          <div className="k">{t('conn.statConnections')}</div>
          <div className="v">{formatNumber(connections.length)}</div>
          <div className="n">
            {isAdmin ? t('conn.statConnectionsAll') : t('conn.statConnectionsMine')}
          </div>
        </div>
        <div className="stat">
          <div className="k">{t('conn.statZones')}</div>
          <div className="v">{formatNumber(totalZones)}</div>
          <div className="n">{t('conn.statZonesNote')}</div>
        </div>
        <div className="stat">
          <div className="k">{t('conn.statBad')}</div>
          <div className="v">{formatNumber(needAttention)}</div>
          <div className="n">{t('conn.statBadNote')}</div>
        </div>
        {isAdmin ? (
          <div className="stat">
            <div className="k">{t('conn.statDefault')}</div>
            <div className="v text">{systemDefault?.name ?? t('conn.noDefault')}</div>
            <div className="n">{t('conn.statDefaultNote')}</div>
          </div>
        ) : null}
      </div>

      <div className="scope-note mt-20">
        <Icon name="info" size={17} />
        <span>{isAdmin ? t('conn.noteAdmin') : t('conn.noteUser')}</span>
      </div>

      <div className="card mt-20" data-od-id="cf-list-card">
        <div className="card-head">
          <div className="toolbar grow">
            <div className="filter-pills" data-od-id="cf-filter">
              {PILLS.map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-pressed={pill === name}
                  onClick={() => datLoc({ status: name === 'all' ? null : name })}
                  data-od-id={`cf-filter-${name}`}
                >
                  {t(`conn.filter.${name}`)}
                  <span className="num"> ({formatNumber(demTheoTrangThai(name))})</span>
                </button>
              ))}
            </div>
            <SearchBox
              value={query}
              onChange={(next) => {
                setQuery(next);
                datLoc({});
              }}
              placeholder={t('conn.searchPlaceholder')}
              width={230}
            />
          </div>
        </div>

        <AsyncSection
          loading={connectionsQuery.isLoading}
          error={connectionsQuery.error}
          data={connectionsQuery.data}
          onRetry={() => void connectionsQuery.refetch()}
          skeleton={<SkeletonCard rows={4} />}
        >
          {() =>
            visible.length === 0 ? (
              <div className="card-body">
                <EmptyState
                  icon="cloud"
                  title={term || pill !== 'all' ? t('conn.emptyFiltered') : t('conn.emptyTitle')}
                  body={
                    term || pill !== 'all'
                      ? undefined
                      : systemDefault
                        ? t('conn.emptyBodyWithDefault', { name: systemDefault.name })
                        : t('conn.emptyBodyNoDefault')
                  }
                  action={
                    term || pill !== 'all' ? undefined : (
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => setDialog({ kind: 'new' })}
                      >
                        {t('conn.addFirst')}
                      </button>
                    )
                  }
                />
              </div>
            ) : (
              <div className="table-wrap">
                <table className="tbl" id="connections-table" data-od-id="cf-table">
                  <thead>
                    <tr>
                      <th scope="col">
                        <SortButton
                          label={t('conn.col.name')}
                          field="name"
                          sort={sort}
                          onSort={(value) => datLoc({ sort: value })}
                          odId="cf-sort-name"
                        />
                      </th>
                      {isAdmin ? (
                        <th scope="col">
                          <SortButton
                            label={t('conn.col.owner')}
                            field="owner"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                            odId="cf-sort-owner"
                          />
                        </th>
                      ) : null}
                      <th scope="col">
                        <SortButton
                          label={t('conn.col.status')}
                          field="status"
                          sort={sort}
                          onSort={(value) => datLoc({ sort: value })}
                          odId="cf-sort-status"
                        />
                      </th>
                      <th scope="col" className="right">
                        <SortButton
                          label={t('conn.col.domains')}
                          field="domains"
                          sort={sort}
                          onSort={(value) => datLoc({ sort: value })}
                          odId="cf-sort-domains"
                        />
                      </th>
                      <th scope="col">{t('conn.accountId')}</th>
                      <th scope="col">{t('conn.token')}</th>
                      <th scope="col">
                        <SortButton
                          label={t('conn.checked')}
                          field="checked"
                          sort={sort}
                          onSort={(value) => datLoc({ sort: value })}
                          odId="cf-sort-checked"
                        />
                      </th>
                      <th scope="col" className="right">
                        {t('conn.col.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((connection) => (
                      <ConnectionRow
                        key={connection.id}
                        connection={connection}
                        isAdmin={isAdmin}
                        canSetDefault={canSetDefault}
                        canWrite={canWrite}
                        busy={verify.isPending}
                        onVerify={() => verify.mutate(connection.id)}
                        onEdit={() => setDialog({ kind: 'edit', connection })}
                        onDelete={() => setDialog({ kind: 'delete', connection })}
                        onDefault={() => setDialog({ kind: 'default', connection })}
                        onTunnels={() => setDialog({ kind: 'tunnels', connection })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </AsyncSection>

        <div className="card-foot">
          <span className="small muted num">
            {t('conn.footCount', { count: formatNumber(total) })}
          </span>
          <span style={{ flex: 1 }} />
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            odId="cf-pager"
            onPage={datTrang}
            onPerPage={datSoDong}
          />
        </div>
      </div>

      {dialog.kind === 'new' ? (
        <NewConnectionModal
          isAdmin={isAdmin}
          canSetDefault={canSetDefault}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={async () => {
            await invalidate();
            setDialog({ kind: 'none' });
            toast(t('conn.created'));
          }}
        />
      ) : null}

      {dialog.kind === 'edit' ? (
        <EditConnectionModal
          connection={dialog.connection}
          onClose={() => setDialog({ kind: 'none' })}
          onRotate={() => setDialog({ kind: 'rotate', connection: dialog.connection })}
          onDone={async () => {
            await invalidate();
            setDialog({ kind: 'none' });
            toast(t('conn.updated'));
          }}
        />
      ) : null}

      {dialog.kind === 'rotate' ? (
        <RotateConnectionModal
          connection={dialog.connection}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={async () => {
            await invalidate();
            setDialog({ kind: 'none' });
            toast(t('conn.rotated'));
          }}
        />
      ) : null}

      {dialog.kind === 'delete' ? (
        <DeleteConnectionModal
          connection={dialog.connection}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={async () => {
            await invalidate();
            setDialog({ kind: 'none' });
            toast(t('conn.deleted'));
          }}
        />
      ) : null}

      {dialog.kind === 'tunnels' ? (
        <TunnelsModal
          connectionId={dialog.connection.id}
          connectionName={dialog.connection.name}
          canWrite={canWrite}
          onClose={() => setDialog({ kind: 'none' })}
        />
      ) : null}

      {dialog.kind === 'default' ? (
        <Modal
          open
          title={t('conn.defaultTitle')}
          onClose={() => setDialog({ kind: 'none' })}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setDialog({ kind: 'none' })}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={setDefault.isPending}
                onClick={() => setDefault.mutate(dialog.connection.id)}
              >
                {setDefault.isPending ? t('common.processing') : t('conn.setDefault')}
              </button>
            </>
          }
        >
          <div className="scope-note accent">
            <Icon name="info" size={17} />
            <span>{t('conn.defaultConfirm', { name: dialog.connection.name })}</span>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function ConnectionRow({
  connection,
  isAdmin,
  canSetDefault,
  canWrite,
  busy,
  onVerify,
  onEdit,
  onDelete,
  onDefault,
  onTunnels,
}: {
  connection: Connection;
  isAdmin: boolean;
  canSetDefault: boolean;
  /** OQ-260 - `conn.own`; ba nut Kiem tra / Sua / Xoa deu la thao tac ghi. */
  canWrite: boolean;
  busy: boolean;
  onVerify: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDefault: () => void;
  onTunnels: () => void;
}) {
  const { formatRelative, formatNumber } = useFormat();
  const t = useT();
  const chip = CONNECTION_STATUS_CHIP[connection.status] ?? 'warn';

  return (
    <tr data-od-id={`conn-${connection.id}`} data-state={connection.status}>
      <td>
        <div className="cell-main">
          <b>{connection.name}</b>
          {connection.isSystemDefault ? (
            <span className="chip ok" data-od-id={`conn-default-${connection.id}`}>
              {t('conn.default')}
            </span>
          ) : null}
        </div>
      </td>
      {/*
        OQ-265 — cột "Email tài khoản" đã bỏ.
        Cloudflare KHÔNG trả email qua API token: `GET /user` chỉ nhận API key
        kiểu cũ, còn `GET /accounts` trả tên tài khoản chứ không phải email (xem
        `checkToken()` và OQ-115). Cột ấy vì thế luôn là "—" trên mọi hàng — một
        cột trống suốt chỉ làm bảng rộng ra và khiến người đọc tưởng dữ liệu bị
        thiếu, trong khi nó chưa bao giờ tồn tại.
      */}
      {isAdmin ? (
        <td className="muted">{connection.owner?.fullName ?? connection.owner?.email ?? '—'}</td>
      ) : null}
      <td>
        <span className={`chip ${chip}`}>
          <span className="dot" />
          {t(`conn.status.${connection.status}`)}
        </span>
      </td>
      <td className="right num">
        {/*
          OQ-265 — con số dẫn thẳng sang danh sách tên miền của kết nối này.

          Trước đây nó chỉ là một con số trần dưới nhãn "Vùng DNS", và không có
          cách nào biết nó gồm những gì. Chủ dự án nhìn 77/1 rồi hỏi "làm thế nào
          để tôi biết kết nối đó có những vùng DNS gì, tôi thấy có vẻ đang sai" —
          câu hỏi đúng, vì một con số không kiểm chứng được thì không đáng tin.
        */}
        {connection.domainCount > 0 ? (
          <Link
            to={`/domains?connectionId=${connection.id}`}
            title={t('conn.domainsLink', { name: connection.name })}
            data-od-id={`conn-domains-${connection.id}`}
          >
            {formatNumber(connection.domainCount)}
          </Link>
        ) : (
          <span className="muted">0</span>
        )}
      </td>
      <td className="mono">{shortenId(connection.accountId)}</td>
      <td className="mono">{`••••${connection.tokenLast4}`}</td>
      <td className="muted">
        {connection.lastCheckedAt ? formatRelative(connection.lastCheckedAt) : t('conn.never')}
      </td>
      <td className="right">
        <div className="row-actions">
          {connection.isSystemDefault ? null : canSetDefault ? (
            <button className="btn btn-sm btn-ghost" type="button" onClick={onDefault}>
              {t('conn.setDefault')}
            </button>
          ) : null}
          <button
            className="btn btn-sm"
            type="button"
            onClick={onVerify}
            disabled={busy || !canWrite}
            title={canWrite ? undefined : t('detail.notAllowed')}
          >
            {t('conn.verify')}
          </button>
          <button
            className="btn btn-sm"
            type="button"
            onClick={onEdit}
            disabled={!canWrite}
            title={canWrite ? undefined : t('detail.notAllowed')}
          >
            {t('common.edit')}
          </button>
          {/*
            OQ-337 — Tunnel.
            Khong can capability moi: ai quan duoc ket noi thi quan duoc tunnel
            cua no. Nut nay MO duoc ca khi chi doc, vi xem danh sach tunnel la
            thao tac doc; ben trong moi chan cac thao tac ghi.
          */}
          <button
            className="btn btn-sm"
            type="button"
            data-od-id={`conn-tunnels-${connection.id}`}
            onClick={onTunnels}
          >
            {t('tunnel.button')}
          </button>
          <button
            className="btn btn-sm btn-danger"
            type="button"
            onClick={onDelete}
            disabled={connection.isSystemDefault || !canWrite}
            title={
              connection.isSystemDefault
                ? t('conn.noteAdmin')
                : canWrite
                  ? undefined
                  : t('detail.notAllowed')
            }
          >
            {t('common.delete')}
          </button>
        </div>
      </td>
    </tr>
  );
}
function shortenId(value: string | null): string {
  if (!value) return '—';
  return value.length <= 14 ? value : `${value.slice(0, 8)}…${value.slice(-4)}`;
}

/* ── Modal: thêm kết nối (FR-102) ───────────────────────────────────── */

/** Chỉ ba trường cần cho ô chọn chủ sở hữu — không kéo cả `User` vào đây. */
interface UserOption {
  id: string;
  email: string;
  fullName?: string | null;
}

function NewConnectionModal({
  isAdmin,
  canSetDefault,
  onClose,
  onDone,
}: {
  isAdmin: boolean;
  canSetDefault: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  /*
   * FR-102 **[A]** — "thêm trường *Thêm thay cho người dùng*". Prototype
   * `cloudflare.html` dùng `<select id="cn-owner">`, không phải ô nhập id: người
   * vận hành không thuộc lòng UUID của đồng nghiệp. Chỉ nạp khi là Admin, vì
   * `GET /users` đòi `adm.users` và trường này cũng chỉ hiện cho Admin.
   */
  const usersQuery = useQuery({
    queryKey: ['users', 'conn-owner-options'],
    queryFn: () => api.get<{ items: UserOption[] }>('/users?pageSize=200'),
    enabled: isAdmin,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<Connection>('/connections', {
        name,
        apiToken,
        // `accountId` để backend tự lấy qua `listAccounts()` khi verify; email
        // Cloudflare không trả qua API token nên cũng không gửi (OQ-115).
        ownerId: ownerId.trim() || null,
        makeDefault,
      }),
    onSuccess: onDone,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('conn.newTitle')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => create.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={create.isPending}>
            {create.isPending ? t('common.processing') : t('conn.save')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <Field
        label={t('conn.name')}
        placeholder={t('conn.namePlaceholder')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={error?.fieldMessage('name')}
        required
      />
      <Field
        label={t('conn.apiToken')}
        type="password"
        autoComplete="off"
        placeholder={t('conn.apiTokenPlaceholder')}
        hint={t('conn.apiTokenHint')}
        value={apiToken}
        onChange={(event) => setApiToken(event.target.value)}
        error={error?.fieldMessage('apiToken')}
        required
      />

      <TokenGuide />

      {/*
       * Account ID và Email KHÔNG còn ở màn thêm (OQ-115).
       *
       * Account ID: hệ thống tự lấy qua `listAccounts()` khi verify, miễn token
       * có quyền `Account → Account Settings → Read` — mà hướng dẫn ở trên đã
       * bắt buộc. Bắt người dùng chép tay một chuỗi 32 ký tự hex mà máy lấy
       * được là việc thừa.
       *
       * Email: Cloudflare **không** trả email qua API token, nên không có cách
       * nào điền hộ. Để ô trống ở đây chỉ tạo cảm giác phải điền. Ai cần ghi
       * chú email thì điền ở màn Sửa.
       */}

      {isAdmin ? (
        <SelectField
          label={t('conn.owner')}
          hint={t('conn.ownerHint')}
          value={ownerId}
          onChange={(event) => setOwnerId(event.target.value)}
          error={error?.fieldMessage('ownerId')}
        >
          <option value="">{t('conn.ownerSelf')}</option>
          {(usersQuery.data?.items ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.fullName ? `${user.fullName} · ${user.email}` : user.email}
            </option>
          ))}
        </SelectField>
      ) : null}

      {canSetDefault ? (
        <div className="setting-row" style={{ paddingTop: 4, borderBottom: 0 }}>
          <div className="grow">
            <b>{t('conn.makeDefault')}</b>
            <div className="sub">{t('conn.makeDefaultSub')}</div>
          </div>
          <label className="switch" aria-label={t('conn.makeDefault')}>
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(event) => setMakeDefault(event.target.checked)}
            />
            <span className="track" />
          </label>
        </div>
      ) : null}
    </Modal>
  );
}

/* ── Modal: sửa kết nối (FR-101, FR-106) ────────────────────────────── */

function EditConnectionModal({
  connection,
  onClose,
  onRotate,
  onDone,
}: {
  connection: Connection;
  onClose: () => void;
  onRotate: () => void;
  onDone: () => Promise<void>;
}) {
  const { formatNumber } = useFormat();
  const t = useT();
  const [name, setName] = useState(connection.name);
  const [paused, setPaused] = useState(connection.status === 'paused');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  /*
   * Tài khoản Cloudflare của token này — OQ-126.
   *
   * Đọc từ Cloudflare mỗi lần mở, không đọc từ CSDL: `cf_connections` chỉ giữ
   * MỘT `cf_account_id` (chọn bừa `accounts[0]` lúc thêm kết nối, OQ-117), và
   * `cf_account_email` thì đang giữ một giá trị SAI từ trước — nó nhận
   * `accounts[0].name` chứ không phải email (OQ-115 mục 1).
   *
   * Dùng lại đúng endpoint của màn Thêm tên miền để một chỗ đúng thì mọi chỗ
   * đúng.
   */
  const accountsQuery = useQuery({
    queryKey: ['domains', 'cf-accounts', connection.id],
    queryFn: () =>
      api.get<{ accounts: CfAccountOption[] }>(
        `/domains/cf-accounts?connectionId=${encodeURIComponent(connection.id)}`,
      ),
  });
  const accounts = accountsQuery.data?.accounts ?? [];

  const save = useMutation({
    mutationFn: () =>
      api.patch<Connection>(`/connections/${connection.id}`, {
        name,
        status: paused ? 'paused' : 'ok',
      }),
    onSuccess: onDone,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('conn.editTitle')}
      subtitle={t('conn.editSub', {
        name: connection.owner?.fullName ?? connection.accountEmail ?? connection.name,
      })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => save.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? t('common.processing') : t('common.saveChanges')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <Field
        label={t('conn.name')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={error?.fieldMessage('name')}
        required
      />
      {/*
       * TÀI KHOẢN CLOUDFLARE — chỉ ĐỌC, và đọc từ Cloudflare — OQ-126.
       *
       * Bản trước là hai ô `<input>` sửa được cho `Account ID` và `Email tài
       * khoản`. Cả hai đều sai về bản chất:
       *
       *  1. Đó là giá trị Cloudflare TRẢ VỀ, không phải người dùng khai. Cho
       *     sửa nghĩa là cho phép ghi một con số không khớp thực tế vào CSDL,
       *     và mọi thứ đọc nó sau đó sẽ sai theo.
       *  2. `Email tài khoản` hiển thị `Ag108` — đó là TÊN tài khoản, không
       *     phải email. Đúng dư âm của OQ-115 mục 1 (`accountEmail` nhận
       *     `accounts[0].name`): mã đã vá nhưng dữ liệu cũ còn nằm lại.
       *  3. Và `accounts[0]` chỉ là MỘT trong nhiều tài khoản token chạm tới
       *     được (OQ-117: token của chủ dự án phủ bốn).
       *
       * Nay liệt kê ĐỦ tài khoản kèm id + tên, lấy trực tiếp từ Cloudflare qua
       * `GET /domains/cf-accounts`. Không ô nhập nào.
       */}
      <div className="field">
        <label>{t('conn.accounts')}</label>
        {accountsQuery.isPending ? (
          <span className="hint">{t('common.processing')}</span>
        ) : accounts.length === 0 ? (
          <span className="hint">{t('conn.accountsNone')}</span>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('conn.accountName')}</th>
                    <th>{t('conn.accountId')}</th>
                    <th className="right">{t('conn.accountZones')}</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <b>{a.name}</b>
                        {a.id.toLowerCase() === (connection.accountId ?? '').toLowerCase() ? (
                          <span className="chip ok" style={{ marginLeft: 8 }}>
                            {t('conn.accountPrimary')}
                          </span>
                        ) : null}
                      </td>
                      <td className="mono small">{a.id}</td>
                      <td className="right num">{formatNumber(a.zoneCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <span className="hint">{t('conn.accountsHint')}</span>
          </>
        )}
      </div>

      <div className="field">
        <label>{t('conn.currentToken')}</label>
        <div className="copy-line">
          <span className="grow mono">cf_tkn_••••••••••••{connection.tokenLast4}</span>
          <button className="btn btn-sm btn-ghost" type="button" onClick={onRotate}>
            {t('conn.rotate')}
          </button>
        </div>
      </div>

      <div className="setting-row" style={{ paddingTop: 4, borderBottom: 0 }}>
        <div className="grow">
          <b>{t('conn.pause')}</b>
          <div className="sub">{t('conn.pauseSub')}</div>
        </div>
        <label className="switch" aria-label={t('conn.pause')}>
          <input
            type="checkbox"
            checked={paused}
            onChange={(event) => setPaused(event.target.checked)}
          />
          <span className="track" />
        </label>
      </div>
    </Modal>
  );
}

/* ── Modal: xoay vòng token (FR-105) ────────────────────────────────── */

function RotateConnectionModal({
  connection,
  onClose,
  onDone,
}: {
  connection: Connection;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const t = useT();
  const [apiToken, setApiToken] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const rotate = useMutation({
    mutationFn: () =>
      api.post<VerifyConnectionResult>(`/connections/${connection.id}/rotate`, { apiToken }),
    onSuccess: onDone,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('conn.rotateTitle')}
      subtitle={t('conn.rotateSub')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => rotate.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={rotate.isPending}>
            {rotate.isPending ? t('common.processing') : t('conn.rotateSave')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <div className="scope-note warn">
        <Icon name="warn" size={17} />
        <span>{t('conn.rotateWarn')}</span>
      </div>
      <Field
        label={t('conn.rotateField')}
        type="password"
        autoComplete="off"
        placeholder={t('conn.apiTokenPlaceholder')}
        value={apiToken}
        onChange={(event) => setApiToken(event.target.value)}
        error={error?.fieldMessage('apiToken')}
        required
      />
    </Modal>
  );
}

/* ── Modal: xoá kết nối (FR-107, BR-6) ──────────────────────────────── */

function DeleteConnectionModal({
  connection,
  onClose,
  onDone,
}: {
  connection: Connection;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { formatNumber } = useFormat();
  const t = useT();
  const [confirmName, setConfirmName] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const remove = useMutation({
    mutationFn: () => api.del<void>(`/connections/${connection.id}`, { confirmName }),
    onSuccess: onDone,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('conn.deleteTitle')}
      subtitle={t('conn.deleteSub', { name: connection.name })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => remove.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-danger"
            type="submit"
            disabled={remove.isPending || confirmName.trim() !== connection.name}
          >
            {remove.isPending ? t('common.processing') : t('conn.deleteTitle')}
          </button>
        </>
      }
    >
      <div className="scope-note warn">
        <Icon name="warn" size={17} />
        <span>{t('conn.deleteWarn', { count: formatNumber(connection.domainCount) })}</span>
      </div>
      <Field
        label={t('conn.deleteConfirm')}
        placeholder={connection.name}
        autoComplete="off"
        value={confirmName}
        onChange={(event) => setConfirmName(event.target.value)}
        error={
          error?.fieldMessage('confirmName') ??
          (error && !error.fields.length ? error.message : undefined)
        }
      />
    </Modal>
  );
}
